import { CommonModule } from '@angular/common';
import { Component, inject, Injectable, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AbstractControl, FormControl, FormGroup, ReactiveFormsModule, ValidationErrors, Validators } from '@angular/forms';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MAT_DIALOG_DATA, MatDialog, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { BaseDialogService, NonNullableConfigData, TilbyDialogContentComponent, TilbyDialogToolbarComponent } from '@tilby/tilby-ui-lib/components/tilby-dialog';
import { TilbyDatetimePickerComponent } from '@tilby/tilby-ui-lib/components/tilby-magic-form';
import { GeneralInputButton } from '@tilby/tilby-ui-lib/models';
import { restManager } from 'app/ajs-upgraded-providers';
import { lastValueFrom } from 'rxjs';
import { EntityManagerService, UserActiveSessionManagerService } from 'src/app/core';
import { MathUtils } from 'src/app/shared/utils';
import { Items, Suppliers } from 'tilby-models';
import { ConfirmDialogService } from '../confirm-dialog';

export type AddEditMovementDialogMode = 'view' | 'production';

export interface AddEditMovementDialogData {
  movement?: Record<string, any> | null;
  productionMode?: boolean;
}

// Esito della modale
export type AddEditMovementDialogResult =
  | { type: 'unchanged'; movement: Record<string, any> }
  | { type: 'updated'; movement: Record<string, any> }
  | { type: 'created'; movement: Record<string, any> }
  | { type: 'duplicate'; movement: Record<string, any> };

interface CombinationOption {
  id: number;
  name: string;
  barcode?: string;
}

interface ProductionPreviewRow {
  id: number;
  name: string;
  quantity: number;
}

const PRODUCTION_CAUSE = 'bom_production';

const toIsoMinute = (date?: Date | string | null): string => {
  const d = date ? new Date(date) : new Date();
  d.setSeconds(0, 0);
  return d.toISOString();
};

const requireSelectedOption = (control: AbstractControl): ValidationErrors | null => {
  const value = control.value;
  return !value || typeof value === 'string' ? { requireMatch: true } : null;
};

const validQuantity = (control: AbstractControl): ValidationErrors | null => {
  return control.value ? null : { invalid: true };
};

@Component({
  selector: 'app-add-edit-movement-dialog',
  standalone: true,
  imports: [
    CommonModule,
    MatAutocompleteModule,
    MatDialogModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    ReactiveFormsModule,
    TilbyDatetimePickerComponent,
    TilbyDialogContentComponent,
    TilbyDialogToolbarComponent,
    TranslateModule
  ],
  templateUrl: './add-edit-movement-dialog.component.html',
  styleUrl: './add-edit-movement-dialog.component.scss'
})
export class AddEditMovementDialogComponent {

  protected readonly data: AddEditMovementDialogData = inject(MAT_DIALOG_DATA);
  private readonly matDialogRef = inject(MatDialogRef<AddEditMovementDialogComponent, AddEditMovementDialogResult>);
  private readonly restManagerService = inject(restManager);
  private readonly entityManager = inject(EntityManagerService);
  private readonly translateService = inject(TranslateService);
  private readonly confirmDialogService = inject(ConfirmDialogService);
  private readonly userActiveSessionManagerService = inject(UserActiveSessionManagerService);

  protected readonly mode: AddEditMovementDialogMode = this.data.productionMode ? 'production' : 'view';
  protected readonly movement: Record<string, any> = this.data.movement ?? {};

  protected readonly title: string = this.mode === 'production'
    ? this.translateService.instant('STOCK.CAUSES.PRODUCTION')
    : (this.movement['name'] ?? '');

  protected readonly sending = signal(false);

  // ------------------- modalità visualizzazione -------------------

  protected viewValues: ReturnType<AddEditMovementDialogComponent['buildViewValues']> | null = null;

  // ------------------- modalità produzione -------------------

  // valorizzati solo in produzione
  protected operatorName = '';
  protected productionItems: Items[] = [];
  protected readonly loadingItems = signal(false);

  protected readonly combinations = signal<CombinationOption[]>([]);
  protected readonly productionPreview = signal<ProductionPreviewRow[]>([]);
  protected readonly entryUnit = signal('');
  protected readonly optionsValues = signal<string | null>(null);

  private barcode: string | null = null;

  protected readonly form = new FormGroup({
    date: new FormControl<string | null>(toIsoMinute(this.movement['date']), { validators: [Validators.required] }),
    entry: new FormControl<Items | string | null>(null),
    combination: new FormControl<CombinationOption | null>(null),
    quantity: new FormControl<number | null>(this.movement['quantity'] ?? null),
    notes: new FormControl<string | null>(this.movement['notes'] ?? null)
  });

  protected customActions: GeneralInputButton[] = [
    {
      name: '',
      isIt: () => true,
      isDisable: () => this.form.invalid || this.sending(),
      icon: signal('check'),
      click: () => this.confirm()
    }
  ];

  protected readonly labelClass = 'tw-text-[12px] tw-font-bold tw-tracking-wider tw-text-[var(--sys-secondary-text)] tw-mb-1';
  protected readonly valueClass = 'tw-text-sm tw-font-medium tw-text-[var(--sys-text)]';

  constructor() {
    if (this.mode === 'view') {
      this.initViewMode();
    } else {
      this.initProductionMode();
    }
  }

  private initViewMode() {
    this.viewValues = this.buildViewValues();
    this.form.controls.date.disable();
    this.form.controls.quantity.disable();

    this.customActions.unshift({
      name: '',
      isIt: () => true,
      icon: signal('content_copy'),
      click: () => this.duplicate()
    });
  }

  private initProductionMode() {
    this.operatorName = this.buildOperatorName();

    this.form.controls.entry.addValidators([Validators.required, requireSelectedOption]);
    this.form.controls.quantity.addValidators([Validators.required, validQuantity]);

    this.form.controls.entry.valueChanges.pipe(takeUntilDestroyed()).subscribe(entry => this.onEntryChange(entry));
    this.form.controls.quantity.valueChanges.pipe(takeUntilDestroyed()).subscribe(() => this.updateProductionPreview());

    this.loadProductionItems();
  }

  private async loadProductionItems() {
    this.loadingItems.set(true);

    try {
      const items = await this.entityManager.items.fetchCollectionOffline({ stock_type: 'bom', manual_production: true }) as Items[];
      this.productionItems = [...items].sort((a, b) => (a.name ?? '').localeCompare(b.name ?? ''));
    } finally {
      this.loadingItems.set(false);
    }
  }

  // ------------------- campi (visualizzazione) -------------------

  private buildViewValues() {
    const movement = this.movement;
    const translate = (key: string) => this.translateService.instant(key);

    return {
      operator: movement['operator_fullname'] || movement['operator_username'] || '',
      type: movement['type'] ? translate(`STOCK.ADD_EDIT_MOVEMENT.${String(movement['type']).toUpperCase()}`) : '',
      supplier: movement['supplier_name'] ?? '',
      supplierOrder: movement['supplier_order'] ?? '',
      cause: this.getCauseText(movement['load_cause'] || movement['unload_cause']),
      entryType: movement['entry_type'] === 'raw_material'
        ? translate('STOCK.ADD_EDIT_MOVEMENT.RAW_MATERIAL')
        : translate('STOCK.ADD_EDIT_MOVEMENT.ITEM'),
      name: movement['name'] ?? '',
      combination: movement['combination'] ?? '',
      barcode: movement['barcode'] ?? '',
      optionsValues: movement['options_values'] ?? ''
    };
  }

  private getCauseText(cause?: string | null): string {
    const unknown = this.translateService.instant('STOCK.CAUSES.UNKNOWN');

    if (!cause) return unknown;

    const key = `STOCK.CAUSES.${cause.toUpperCase()}`;
    const translation = this.translateService.instant(key);

    return translation === key ? unknown : translation;
  }

  private buildOperatorName(): string {
    const user = this.userActiveSessionManagerService.getSession();

    return user?.first_name && user?.last_name ? `${user.first_name} ${user.last_name}` : (user?.username ?? '');
  }

  // ------------------- produzione -------------------

  protected filteredItems(): Items[] {
    const value = this.form.controls.entry.value;
    const text = (typeof value === 'string' ? value : '').trim().toLowerCase();

    return text
      ? this.productionItems.filter(item => [item.name, item.sku, item.code].some(field => String(field ?? '').toLowerCase().includes(text)))
      : this.productionItems;
  }

  protected displayEntry = (entry: Items | string | null): string => {
    return entry && typeof entry === 'object' ? (entry.name ?? '') : (entry ?? '');
  };

  private onEntryChange(entry: Items | string | null) {
    this.barcode = null;
    this.optionsValues.set(null);
    this.combinations.set([]);
    this.form.controls.combination.reset(null, { emitEvent: false });
    this.form.controls.combination.clearValidators();
    this.form.controls.combination.updateValueAndValidity({ emitEvent: false });

    if (entry && typeof entry === 'object') {
      const combinations = this.parseCombinations(entry);

      this.combinations.set(combinations);
      // se il prodotto ha combinazioni, sceglierne una è obbligatorio
      if (combinations.length) {
        this.form.controls.combination.addValidators(Validators.required);
        this.form.controls.combination.updateValueAndValidity({ emitEvent: false });
      }

      this.optionsValues.set(this.parseItemOptionsValues(entry));
      this.barcode = entry.barcodes?.[0]?.barcode ?? null;
      this.entryUnit.set(entry.unit ? ` (${entry.unit})` : '');
    } else {
      this.entryUnit.set('');
    }

    this.updateProductionPreview();
  }

  protected compareCombinations = (a: CombinationOption | null, b: CombinationOption | null) => a?.id === b?.id;

  protected onCombinationChange() {
    const barcode = this.form.controls.combination.value?.barcode;

    if (barcode) {
      this.barcode = barcode;
    }
  }

  private parseCombinations(item: Items): CombinationOption[] {
    return (item.combinations ?? []).map(combination => ({
      id: combination.id!,
      name: (combination.combination_values ?? []).map((value: any) => value.variation_value).join(', '),
      barcode: combination.barcodes?.[0]?.barcode
    }));
  }

  private parseItemOptionsValues(item: Record<string, any>): string | null {
    const values: string[] = [];

    for (let i = 1; i <= 5; i++) {
      if (item[`option${i}_name`] && item[`option${i}_value`]) {
        values.push(item[`option${i}_value`]);
      }
    }

    return values.length ? values.join(', ') : null;
  }

  // componenti della distinta base che verranno scaricati, in base alla quantità prodotta
  private updateProductionPreview() {
    const entry = this.form.controls.entry.value;

    if (!entry || typeof entry !== 'object') {
      return this.productionPreview.set([]);
    }

    const result: ProductionPreviewRow[] = [];

    const scan = (bomComponents: any[] | undefined, quantity: number) => {
      (bomComponents ?? []).forEach(component => {
        if (component.component_item_id || component.component_raw_material_id) {
          result.push({ id: component.id, name: component.name, quantity: MathUtils.round(component.quantity * quantity, 4) });
        } else {
          scan(component.bom_components, quantity * component.quantity);
        }
      });
    };

    scan((entry as any).bom_components, this.form.controls.quantity.value || 0);
    this.productionPreview.set(result);
  }

  // ------------------- conferma -------------------

  protected async confirm() {
    if (this.mode === 'view') {
      return this.confirmView();
    }

    return this.confirmProduction();
  }

  // in visualizzazione l'unica modifica possibile è la nota
  private async confirmView() {
    const notes = this.form.controls.notes.value;

    if (notes === (this.movement['notes'] ?? null)) {
      return this.matDialogRef.close({ type: 'unchanged', movement: this.movement } as AddEditMovementDialogResult);
    }

    this.sending.set(true);

    try {
      const updated = await this.restManagerService.put('stock_movements', this.movement['id'], { ...structuredClone(this.movement), notes });
      this.matDialogRef.close({ type: 'updated', movement: updated } as AddEditMovementDialogResult);
    } finally {
      this.sending.set(false);
    }
  }

  private async confirmProduction() {
    const entry = this.form.controls.entry.value as Items;

    // la produzione non ha un fornitore: se il prodotto ne ha uno predefinito si chiede conferma
    if (entry.default_supplier_id) {
      const suppliers = await this.entityManager.suppliers.fetchCollectionOffline() as Suppliers[];
      const defaultSupplier = suppliers.find(supplier => supplier.id === entry.default_supplier_id);

      const confirmed = await this.confirmDialogService.openDialog({
        data: {
          messageLabel: 'STOCK.ADD_EDIT_MOVEMENT.SUPPLIER_WARNING',
          messageParams: { supplier: defaultSupplier?.name ?? this.translateService.instant('STOCK.CAUSES.UNKNOWN') }
        }
      });

      if (!confirmed) return;
    }

    this.sending.set(true);

    try {
      const created = await this.restManagerService.post('stock_movements', this.buildProductionMovement(entry));
      this.matDialogRef.close({ type: 'created', movement: created } as AddEditMovementDialogResult);
    } finally {
      this.sending.set(false);
    }
  }

  private buildProductionMovement(entry: Items): Record<string, any> {
    const { date, quantity, notes, combination } = this.form.getRawValue();
    const user = this.userActiveSessionManagerService.getSession();

    const movement: Record<string, any> = {
      entry_type: 'item',
      type: 'load',
      load_cause: PRODUCTION_CAUSE,
      date: new Date(date!).toISOString(),
      quantity,
      notes: notes ?? undefined,
      name: entry.name,
      unit: entry.unit,
      item_id: entry.id,
      code: entry.code,
      barcode: this.barcode ?? undefined,
      options_values: this.optionsValues() ?? undefined,
      operator_id: user?.id
    };

    if (user?.first_name && user?.last_name) {
      movement['operator_fullname'] = `${user.first_name} ${user.last_name}`;
    } else {
      movement['operator_username'] = user?.username;
    }

    if (this.combinations().length && combination) {
      movement['combination_id'] = combination.id;
      movement['combination'] = combination.name;
    }

    return movement;
  }

  private duplicate() {
    this.matDialogRef.close({ type: 'duplicate', movement: this.movement } as AddEditMovementDialogResult);
  }
}

@Injectable({
  providedIn: 'root',
})
export class AddEditMovementDialogService extends BaseDialogService {
  private readonly dialogRef = inject(MatDialog);

  public openDialog(config: NonNullableConfigData<AddEditMovementDialogData>): Promise<AddEditMovementDialogResult | undefined> {
    const configEdited: NonNullableConfigData<AddEditMovementDialogData> = {
      ...config,
      ...this.switchMobileDesktopDimensions({ width: "80vw", maxWidth: "1200px" }, { fullScreenForMobile: true }),
      disableClose: true,
    };

    return lastValueFrom(
      this.dialogRef.open(AddEditMovementDialogComponent, configEdited).afterClosed()
    );
  }
}
